"use client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useState } from "react";
import { signIn } from "@/lib/auth-client";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import * as Sentry from "@sentry/browser";
import { trackEvent } from "@/lib/analytics";
import { ExternalLinkIcon } from "lucide-react";
import type { BetterFetchError } from "better-auth/react";
const signInSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  rememberMe: z.boolean(),
});
type SignInFormData = z.infer<typeof signInSchema>;
type SignInFormProps = {
  onSuccess?: () => void;
};
function isSupportedSignInHost(hostname: string) {
  return hostname === "localhost" || hostname === "scoopulator.app";
}
function captureAuthError(
  error: BetterFetchError,
  provider: "email" | "google",
) {
  Sentry.captureException(error, {
    extra: {
      provider,
      status: error.status,
      statusText: error.statusText,
      originalError: error,
    },
  });
}
export function SignInForm({ onSuccess }: SignInFormProps) {
  const [loading, setLoading] = useState(false);
  const [isUnsupportedSignInHost, setIsUnsupportedSignInHost] = useState(false);
  useEffect(() => {
    setIsUnsupportedSignInHost(
      !isSupportedSignInHost(window.location.hostname),
    );
  }, []);
  const form = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: true,
    },
  });
  const onSubmit = async (data: SignInFormData) => {
    setLoading(true);
    try {
      trackEvent("sign-in-submit");
      await signIn.email(
        {
          email: data.email,
          password: data.password,
        },
        {
          onResponse: () => {
            setLoading(false);
          },
          onError: (ctx) => {
            if (ctx.error.status !== 401) {
              captureAuthError(ctx.error, "email");
            }
            toast.error(
              ctx.error.message || "Something went wrong. Please try again.",
            );
            setLoading(false);
            trackEvent("sign-in-error", {
              provider: "email",
              ["error-message"]: ctx.error.message,
            });
          },
          onSuccess: () => {
            toast.success("Signed in successfully!");
            trackEvent("sign-in-success", {
              provider: "email",
            });
            onSuccess?.();
          },
        },
      );
    } catch (error) {
      Sentry.captureException(error);
      setLoading(false);
      toast.error("An unexpected error occurred");
    }
  };
  return (
    <Form {...form}>
      {isUnsupportedSignInHost ? (
        <Alert variant="warning" className="mb-4">
          <AlertTitle>Sign in on Scoopulator directly</AlertTitle>
          <AlertDescription>
            You are on an unsupported domain. You may be using a translation
            service or a third-party website. Please go to{" "}
            <a
              className="underline-link"
              href="https://scoopulator.app"
              target="_blank"
              rel="noopener noreferrer"
            >
              scoopulator.app
            </a>{" "}
            to sign in.
          </AlertDescription>
          <Button
            className="mt-4"
            variant="outline"
            size="sm"
            render={
              <a
                href="https://scoopulator.app"
                target="_blank"
                rel="noopener noreferrer"
              />
            }
          >
            Open Scoopulator to sign in <ExternalLinkIcon />
          </Button>
        </Alert>
      ) : null}

      <div className="mt-4">
        <Button
          variant="outline"
          className={cn("w-full gap-2")}
          busy={loading}
          type="button"
          onClick={async () => {
            setLoading(true);
            try {
              await signIn.social(
                {
                  provider: "google",
                  callbackURL: window.location.href,
                },
                {
                  onResponse: () => {
                    setLoading(false);
                  },
                  onError: (ctx) => {
                    captureAuthError(ctx.error, "google");
                    toast.error(
                      ctx.error.message ||
                        "Something went wrong. Please try again.",
                    );
                    setLoading(false);
                    trackEvent("sign-in-error", {
                      provider: "google",
                      ["error-message"]: ctx.error.message,
                    });
                  },
                  onSuccess: () => {
                    toast.success("Signed in successfully!");
                    trackEvent("sign-in-success", {
                      provider: "google",
                    });
                  },
                },
              );
            } catch (error) {
              Sentry.captureException(error);
              setLoading(false);
              toast.error("An unexpected error occurred");
            }
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="0.98em"
            height="1em"
            viewBox="0 0 256 262"
          >
            <path
              fill="#4285F4"
              d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
            ></path>
            <path
              fill="#34A853"
              d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
            ></path>
            <path
              fill="#FBBC05"
              d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z"
            ></path>
            <path
              fill="#EB4335"
              d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
            ></path>
          </svg>
          Sign in with Google
        </Button>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="bg-border h-px w-full" aria-hidden="true" />
        <span className="text-muted-foreground px-2">or</span>
        <div className="bg-border h-px w-full" aria-hidden="true" />
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="john.doe@example.com"
                  autoComplete="email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center">
                <FormLabel>Password</FormLabel>
                <Link
                  to="/reset-password"
                  className="text-muted-foreground ml-auto inline-block text-sm underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Forgot your password?
                </Link>
              </div>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Password"
                  autoComplete="current-password"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="rememberMe"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-y-0 space-x-3">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Remember me</FormLabel>
              </div>
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" busy={loading}>
          Login
        </Button>
      </form>

      <p className="text-muted-foreground text-sm">
        Don&apos;t have an account?{" "}
        <Link className="underline" to="/sign-up" target="_blank">
          Sign up
        </Link>
      </p>
    </Form>
  );
}
